// agentModeScroll.js — the content's scroll and the model's rows
// (WS-AGENT-MODE-1).
//
// A detail document and the Ask AI conversation scroll by keys in the model
// (j / k, g / G, PgUp / PgDn move `view.top`, in rows). The DOM scrolls
// natively. Two moves keep them one (the package README "viewport()"):
//   - the model's row → the scroller: when `view.top` changes, the content
//     scrolls to top × its line height (agentModeSyncScroll);
//   - the scroller → the model: the sizes the page keys read (contentRows,
//     docRows) and, after a native scroll, the row now at the top
//     (agentModeReportScroll → runtime.viewport).
// Lists and boards need neither: their rows reveal themselves
// (agentModeRevealSelected).
// The scroller moves through `scrollTo()` — reading and scrolling `el.node`
// is allowed; a property write (`node.scrollTop = …`) is frank FA511.

// The line height of a node in px (the frame's mono line), or null.
export const agentModeLineHeight = (node) => {
  if (!node || !node.ownerDocument || !node.ownerDocument.defaultView) return null
  const cs = node.ownerDocument.defaultView.getComputedStyle(node)
  const lh = parseFloat(cs.lineHeight)
  if (Number.isFinite(lh) && lh > 0) return lh
  const fs = parseFloat(cs.fontSize)
  return Number.isFinite(fs) && fs > 0 ? fs * 1.5 : null
}

// The model's row → the scroller (detail, Ask AI).
export function agentModeSyncScroll(el) {
  const root = el && el.state && el.state.root
  const node = el && el.node
  if (!root || !node) return
  const snap = this.call('agentModeSnapshot', root)
  const content = snap && snap.content
  const view = content && content.view
  if (!view || (content.type !== 'detail' && content.type !== 'assistant')) return
  const lh = this.call('agentModeLineHeight', node)
  if (!lh) return
  const scope = el.scope || {}
  queueMicrotask(() => {
    if (!node.isConnected) return
    // Ask AI follows the newest line until the reader scrolls up.
    if (content.type === 'assistant' && view.follow) {
      if (typeof node.scrollTo === 'function') node.scrollTo({ top: node.scrollHeight })
      return
    }
    const top = Number(view.top) || 0
    if (scope.agentModeTop === top) return
    scope.agentModeTop = top
    if (typeof node.scrollTo === 'function') node.scrollTo({ top: top * lh })
  })
}

// The scroller → the model: sizes for the page keys, the row at the top.
export function agentModeReportScroll(el, opts) {
  const root = el && el.state && el.state.root
  const node = el && el.node
  const runtime = root && root.__agentModeRuntime
  if (!runtime || typeof runtime.viewport !== 'function' || !node) return
  const lh = this.call('agentModeLineHeight', node)
  if (!lh) return
  const top = Math.round(node.scrollTop / lh)
  if (el.scope) el.scope.agentModeTop = top
  runtime.viewport({
    contentRows: Math.max(1, Math.floor(node.clientHeight / lh)),
    docRows: Math.max(1, Math.ceil(node.scrollHeight / lh)),
    ...(opts && opts.top ? { top } : {})
  })
}

// The key help scrolls by lines in the model (↓ j / ↑ k move `ui.top`; smbls
// packages/tui shell/overlays.js drawHelp). The help draws one line per TUI
// line, so the model's line → the scroller is `top` × the line height, and
// the renderer clamps `ui.top` to the last full page, as drawHelp does.
export function agentModeSyncHelpScroll(el) {
  const root = el && el.state && el.state.root
  const node = el && el.node
  if (!root || !node) return
  const o = this.call('agentModeOverlayModel', root)
  if (!o || o.kind !== 'help' || !o.ui) return
  const lh = this.call('agentModeLineHeight', node)
  if (!lh) return
  const ui = o.ui
  const lines = Number(o.lines) || 0
  queueMicrotask(() => {
    if (!node.isConnected) return
    const shown = Math.max(1, Math.floor(node.clientHeight / lh))
    const top = Math.max(0, Math.min(Number(ui.top) || 0, Math.max(0, lines - shown)))
    ui.top = top
    if (typeof node.scrollTo === 'function') node.scrollTo({ top: top * lh })
  })
}

// A wheel or a drag scrolled the help: the model's line follows, so the next
// ↓ / ↑ moves on from there.
export function agentModeReportHelpScroll(el) {
  const root = el && el.state && el.state.root
  const node = el && el.node
  if (!root || !node) return
  const o = this.call('agentModeOverlayModel', root)
  if (!o || o.kind !== 'help' || !o.ui) return
  const lh = this.call('agentModeLineHeight', node)
  if (!lh) return
  o.ui.top = Math.round(node.scrollTop / lh)
}
